import { useQuery } from "@apollo/client/react";
import PENDING_WRITING_COMPANION_PROPOSALS from "@graphql/queries/PendingWritingCompanionProposals";
import {
  createContext,
  type ReactNode,
  useContext,
  useState,
} from "react";

/**
 * An edit proposal awaiting accept/reject — from the Companion (`proposeLlmEdit`)
 * or the in-app writing assistant (`generateLlmCustomizations`). Mirrors brian's
 * `WritingCompanionProposal` GraphQL type (`pendingWritingCompanionProposals`);
 * `uuid` is the `llm_customization_uuid` the WS completion event carries.
 */
export interface PendingWritingCompanionProposal {
  uuid: string;
  noteUuid: string;
  noteSection: string;
  afterContent: string | null;
}

export interface PendingWritingCompanionProposals {
  proposals: PendingWritingCompanionProposal[];
  /** Drop a proposal locally once accept/reject has been sent for it */
  resolveProposal: (uuid: string) => void;
}

const NO_PROPOSALS: PendingWritingCompanionProposals = {
  proposals: [],
  resolveProposal: () => {},
};

const PendingWritingCompanionProposalsContext =
  createContext<PendingWritingCompanionProposals>(NO_PROPOSALS);

/**
 * Loads the note's unresolved proposals (Companion and writing assistant) once
 * per note so section editors can render diffs the user never resolved.
 */
export function usePendingWritingCompanionProposalsQuery(
  noteUuid: string | undefined,
): PendingWritingCompanionProposals {
  const { data } = useQuery(PENDING_WRITING_COMPANION_PROPOSALS, {
    variables: { noteUuid: noteUuid ?? "" },
    skip: !noteUuid,
    fetchPolicy: "network-only",
  });
  const [resolvedUuids, setResolvedUuids] = useState<string[]>([]);

  const proposals = (
    data?.pendingWritingCompanionProposals ?? []
  ).filter((proposal) => !resolvedUuids.includes(proposal.uuid));

  const resolveProposal = (uuid: string) => {
    setResolvedUuids((prev) =>
      prev.includes(uuid) ? prev : [...prev, uuid],
    );
  };

  return { proposals, resolveProposal };
}

/** Makes a note's pending proposals available to every `useWritingAssistant` beneath it. */
export function PendingWritingCompanionProposalsProvider({
  value,
  children,
}: {
  value: PendingWritingCompanionProposals;
  children: ReactNode;
}) {
  return (
    <PendingWritingCompanionProposalsContext.Provider value={value}>
      {children}
    </PendingWritingCompanionProposalsContext.Provider>
  );
}

/** Pending proposals for the surrounding note; empty outside a provider (e.g. templates). */
export function usePendingWritingCompanionProposals(): PendingWritingCompanionProposals {
  return useContext(PendingWritingCompanionProposalsContext);
}

/** Newest pending proposal targeting `section` of `noteUuid`, if any */
export function selectPendingProposalForSection(
  proposals: PendingWritingCompanionProposal[],
  noteUuid: string | undefined,
  section: string,
): PendingWritingCompanionProposal | undefined {
  if (!noteUuid) return undefined;
  // brian returns oldest first; when a section has several pending rows the last one is current.
  for (let i = proposals.length - 1; i >= 0; i--) {
    const proposal = proposals[i];
    if (
      proposal.noteUuid === noteUuid &&
      proposal.noteSection.toLowerCase() === section.toLowerCase()
    ) {
      return proposal;
    }
  }
  return undefined;
}
